import React from 'react';
import { useTranslation } from 'react-i18next';
import ayurvedaImage from '../assets/Ayurveda/Blog-Cover-3.jpg';
import ayurvedaFaceImage from '../assets/Ayurveda/a-guide-to-sri-lanka-wellness-holidays-part-3-ayurveda-header.jpg';
import ayurvedaMassageImage from '../assets/Ayurveda/Spa-&-Ayurveda-header.jpg';

const AyurvedaPage = () => {
    const { t } = useTranslation();

    return (
        <div className="py-16 px-4 sm:px-8 lg:px-16 bg-gradient-to-b from-[#FFFFFF] to-[#F8F1E9] min-h-screen font-customFont">
            {/* Title */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-center text-[#122C56] mb-12 tracking-tight drop-shadow-md animate-fade-in">
                {t('ayurvedaPage.title')}
            </h1>

            {/* Main Section */}
            <div className="max-w-7xl mx-auto mt-8 flex flex-col lg:flex-row gap-8 items-center">
                {/* Left Section: Text Content */}
                <div className="flex-1 bg-white p-8 rounded-2xl shadow-xl">
                    <p className="text-gray-700 text-base sm:text-lg md:text-xl leading-relaxed mb-6">
                        {t('ayurvedaPage.mainDescription')}
                    </p>
                    <ul className="list-disc list-inside text-gray-700 text-base sm:text-lg leading-relaxed space-y-2">
                        <li>{t('ayurvedaPage.principles.energyBalance')}</li>
                        <li>{t('ayurvedaPage.principles.muscleHormonal')}</li>
                        <li>{t('ayurvedaPage.principles.immuneSystem')}</li>
                    </ul>
                    <p className="mt-6 text-gray-700 text-base sm:text-lg md:text-xl leading-relaxed">
                        {t('ayurvedaPage.trainingDescription')}
                    </p>
                    <ul className="list-disc list-inside mt-4 text-gray-700 text-base sm:text-lg leading-relaxed space-y-2">
                        <li>{t('ayurvedaPage.programs.stressRelief')}</li>
                        <li>{t('ayurvedaPage.programs.oxygenSaturation')}</li>
                        <li>{t('ayurvedaPage.programs.muscularToxins')}</li>
                        <li>{t('ayurvedaPage.programs.nervousSystem')}</li>
                        <li>{t('ayurvedaPage.programs.bloodFlow')}</li>
                        <li>{t('ayurvedaPage.programs.endorphins')}</li>
                    </ul>
                </div>

                {/* Right Section: Main Image */}
                <div className="w-full lg:w-1/3">
                    <div className="relative overflow-hidden rounded-2xl shadow-xl group">
                        <img
                            src={ayurvedaImage}
                            alt={t('ayurvedaPage.images.mainAlt')}
                            className="w-full h-64 sm:h-80 md:h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#122C56] to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-300"></div>
                    </div>
                </div>
            </div>

            {/* Additional Images Section */}
            <div className="max-w-7xl mx-auto mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* First Additional Image */}
                <div className="relative overflow-hidden rounded-2xl shadow-xl group">
                    <img
                        src={ayurvedaFaceImage}
                        alt={t('ayurvedaPage.images.faceTreatmentAlt')}
                        className="w-full h-64 sm:h-80 md:h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#122C56] to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-300"></div>
                    <div className="absolute bottom-0 left-0 right-0 bg-[#F8F1E9] bg-opacity-90 p-4">
                        <p className="text-[#122C56] text-sm sm:text-base font-semibold text-center">
                            {t('ayurvedaPage.images.faceTreatmentAlt')}
                        </p>
                    </div>
                </div>

                {/* Second Additional Image */}
                <div className="relative overflow-hidden rounded-2xl shadow-xl group">
                    <img
                        src={ayurvedaMassageImage}
                        alt={t('ayurvedaPage.images.massageTreatmentAlt')}
                        className="w-full h-64 sm:h-80 md:h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#122C56] to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-300"></div>
                    <div className="absolute bottom-0 left-0 right-0 bg-[#F8F1E9] bg-opacity-90 p-4">
                        <p className="text-[#122C56] text-sm sm:text-base font-semibold text-center">
                            {t('ayurvedaPage.images.massageTreatmentAlt')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Treatment Section */}
            <div className="max-w-7xl mx-auto mt-12 bg-white p-8 rounded-2xl shadow-xl">

                <p className="text-gray-700 text-base sm:text-lg md:text-xl leading-relaxed">
                    {t('ayurvedaPage.treatmentDescription')}
                </p>
            </div>

            {/* Tips Section */}
            <div className="max-w-7xl mx-auto mt-12 bg-white p-8 rounded-2xl shadow-xl">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#122C56] mb-6 drop-shadow-md">
                    {t('ayurvedaPage.tipsTitle')}
                </h2>
                <ul className="list-none space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
                    {[
                        t('ayurvedaPage.tips.relaxMind'),
                        t('ayurvedaPage.tips.chooseLocation'),
                        t('ayurvedaPage.tips.selectProgram'),
                        t('ayurvedaPage.tips.warmBath'),
                        t('ayurvedaPage.tips.lightMeal'),
                        t('ayurvedaPage.tips.postRelaxation'),
                    ].map((tip, index) => (
                        <li key={index} className="flex items-center">
                            <span className="w-2 h-2 bg-[#C58E2F] rounded-full mr-2"></span>
                            {tip}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default AyurvedaPage;